import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// League Fee Leakage — Sprint 21.4. Limited ("Play", $89/mo) plan members get
// 50% off leagues/tournaments/p-series, never free (confirmed from the real
// Picklr plan-benefit screens). A $0 total on one of these bookings means the
// 50% discount was never applied. Mirrors CompQueue.jsx's pattern exactly.

function LeagueFeeCard({ b, onReview, updating }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(b.note || '');

  return (
    <div style={{
      background: 'var(--surface-2, #111)',
      border: `1px solid ${b.reviewStatus === 'flagged' ? '#ef444440' : 'var(--border, #222)'}`,
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)' }}>{b.name}</span>
            <span style={{
              background: '#ffffff11', color: '#aaa', border: '1px solid #333',
              borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap',
            }}>
              {b.membershipType}
            </span>
          </div>
          <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>
            {b.email} · {new Date(b.bookingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {b.locationId}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {b.activityType} — {b.bookingId} — charged $0, should be 50% off
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            disabled={updating}
            onClick={() => onReview(b.id, b.locationId, 'cleared', note)}
            style={{
              background: b.reviewStatus === 'cleared' ? '#22c55e' : 'var(--surface-3, #1a1a1a)',
              color: b.reviewStatus === 'cleared' ? '#000' : '#22c55e',
              border: '1px solid #22c55e', borderRadius: 6,
              padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >Clear</button>
          <button
            disabled={updating}
            onClick={() => onReview(b.id, b.locationId, 'flagged', note)}
            style={{
              background: b.reviewStatus === 'flagged' ? '#ef4444' : 'var(--surface-3, #1a1a1a)',
              color: b.reviewStatus === 'flagged' ? '#000' : '#ef4444',
              border: '1px solid #ef4444', borderRadius: 6,
              padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >Flag</button>
          <button
            onClick={() => setNoteOpen(o => !o)}
            style={{
              background: 'var(--surface-3, #1a1a1a)', color: 'var(--text-muted)',
              border: '1px solid var(--border, #333)', borderRadius: 6,
              padding: '5px 10px', fontSize: 11, cursor: 'pointer',
            }}
          >{noteOpen ? '▲' : '📝'}</button>
        </div>
      </div>
      {noteOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
          <input
            className="field-input" type="text" placeholder="Add a note..."
            value={note} onChange={e => setNote(e.target.value)}
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            disabled={updating}
            onClick={() => onReview(b.id, b.locationId, b.reviewStatus, note)}
            style={{
              background: 'var(--yellow)', color: '#000', border: 'none',
              borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >Save Note</button>
        </div>
      )}
      {b.note && !noteOpen && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>"{b.note}"</div>
      )}
    </div>
  );
}

export default function LeagueFeeLeakage() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [updating, setUpdating] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const clubId = currentClub?.location_id || currentClub?.id;
    get(`/revenue/league-fee-leakage?club=${clubId}`)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- get from useApi() is a new reference every render; including it causes an infinite fetch loop (confirmed live, 9/30/26)

  useEffect(() => { load(); }, [load]);

  async function handleReview(bookingRowId, locationId, status, note) {
    setUpdating(prev => ({ ...prev, [bookingRowId]: true }));
    try {
      await post(`/revenue/league-fee-leakage/${bookingRowId}/review`, { locationId, status, note });
      setData(prev => prev ? ({
        ...prev,
        bookings: prev.bookings.map(b => b.id === bookingRowId ? { ...b, reviewStatus: status, note } : b),
      }) : prev);
    } catch (e) {
      console.error('League fee review update failed', e);
    } finally {
      setUpdating(prev => ({ ...prev, [bookingRowId]: false }));
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load League Fee Leakage — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const all = data?.bookings || [];
  const filtered = all.filter(b => filter === 'all' || b.reviewStatus === 'pending');
  const pendingCount = all.filter(b => b.reviewStatus === 'pending').length;

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          ⚠️ <strong style={{ color: 'var(--text)' }}>{pendingCount}</strong> pending review · {all.length} total Limited-tier $0 league/tournament bookings
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['pending', 'all'].map(f => (
            <button
              key={f} onClick={() => setFilter(f)}
              style={{
                background: filter === f ? 'var(--yellow)' : 'var(--surface-2)',
                color: filter === f ? '#000' : 'var(--text-muted)',
                border: '1px solid var(--border)', borderRadius: 6,
                padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
              }}
            >{f}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 14 }}>
        Limited ("Play") plan members are entitled to 50% off leagues/tournaments/p-series, never free. A $0 total here
        means the discount was never applied at checkout.
      </div>

      {filtered.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          No {filter === 'pending' ? 'pending' : ''} league fee gaps to review.
        </div>
      ) : (
        filtered.map(b => <LeagueFeeCard key={b.id} b={b} onReview={handleReview} updating={updating[b.id]} />)
      )}
    </div>
  );
}
